import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { LoginCredentials } from '../../../types';
import { getDefaultLandingPage } from '../../../config/permissionBasedNavigation';
import { getRedirectPath } from '../../../utils/sessionContinuity';
import { Eye, EyeOff, Settings, Search, Bot, Sprout, Play } from 'lucide-react';

interface DemoAccount {
  email: string;
  name: string;
  role: string;
  description: string;
  company: string | null;
  group: 'platform' | 'partner' | 'partner_portfolio';
}

const C = {
  bg:     '#030712',
  bg2:    '#05072a',
  bg3:    '#0a0e35',
  card:   'rgba(255,255,255,0.04)',
  border: 'rgba(255,255,255,0.08)',
  lime:   '#bef264',
  cyan:   '#67e8f9',
  white:  '#ffffff',
  gray1:  '#e2e8f0',
  gray2:  '#94a3b8',
  gray3:  '#475569',
};

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '12px 14px',
  background: C.bg3, border: `1px solid ${C.border}`,
  borderRadius: 10, color: C.white, fontSize: 14,
  outline: 'none', boxSizing: 'border-box',
  fontFamily: 'Inter, system-ui, sans-serif',
};

const Login: React.FC = () => {
  const [credentials, setCredentials] = useState<LoginCredentials>({ email: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [demoAccounts, setDemoAccounts] = useState<DemoAccount[]>([]);
  const [demoLoading, setDemoLoading] = useState<string | null>(null);
  const { login, user, hasPermission, userDomain } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user) {
      const defaultLandingPage = getDefaultLandingPage(hasPermission, userDomain);
      const redirectPath = getRedirectPath(defaultLandingPage);
      if (redirectPath) {
        navigate(redirectPath, { replace: true });
      } else {
        setIsLoggingIn(false);
        setLoginError('Your account has no permissions assigned yet. Ask your administrator to assign a permission profile.');
      }
    }
  }, [user, hasPermission, userDomain, navigate]);

  // Fetch demo accounts if demo mode is enabled
  useEffect(() => {
    fetch('/api/auth/demo-accounts')
      .then(r => r.json())
      .then(d => { if (d?.data?.enabled) setDemoAccounts(d.data.accounts); })
      .catch(() => {});
  }, []);

  const handleDemoLogin = async (email: string) => {
    setDemoLoading(email);
    setLoginError(null);
    try {
      const res = await fetch('/api/auth/demo-login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });
      const data = await res.json();
      if (data.success) {
        // Store token and user info same as normal login
        localStorage.setItem('token', data.token);
        localStorage.setItem('refreshToken', data.refreshToken);
        localStorage.setItem('user', JSON.stringify(data.user));
        // Redirect to the correct dashboard based on permissions
        const perms = data.user?.permissions || [];
        const hasCompany = !!data.user?.company_id;
        const hasPartner = !!data.user?.partner_id;
        let dest = '/';
        if (!hasCompany && hasPartner && perms.includes('partner.dashboard.view')) dest = '/partner/dashboard';
        else if (!hasCompany && perms.includes('admin.dashboard.view')) dest = '/admin/dashboard';
        else if (hasCompany && perms.includes('company.dashboard.view')) dest = '/company/dashboard';
        else if (hasCompany && perms.includes('audit.dashboard.view')) dest = '/audit/dashboard';
        else if (hasCompany && perms.includes('compliance.dashboard.view')) dest = '/compliance/dashboard';
        else if (!hasCompany && perms.includes('audit.dashboard.view')) dest = '/audit/dashboard';
        window.location.href = dest;
      } else {
        setLoginError(data.message || 'Demo login failed');
      }
    } catch {
      setLoginError('Demo login failed. Please try again.');
    } finally {
      setDemoLoading(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoginError(null);
    setIsLoggingIn(true);
    try {
      await login(credentials.email, credentials.password);
    } catch (error: any) {
      const errorMessage = error.message || 'Login failed. Please try again.';
      if (error.requiresTwoFactor || error.requiresTwoFactorSetup) {
        // 2FA code sent to email — redirect to verify page
        navigate('/2fa-verify');
        return;
      } else if (errorMessage.includes('Too many login attempts') || errorMessage.includes('Too many requests')) {
        setLoginError('Too many login attempts. Please wait 15 minutes before trying again.');
      } else {
        setLoginError(errorMessage);
      }
      setIsLoggingIn(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setCredentials(prev => ({ ...prev, [name]: value }));
  };

  return (
    <div style={{ minHeight: '100vh', backgroundColor: C.bg, fontFamily: 'Inter, system-ui, sans-serif', color: C.white, display: 'flex', alignItems: 'stretch', justifyContent: 'center' }}>
    <div className="login-root" style={{ width: '100%', maxWidth: 1420, display: 'grid', gridTemplateColumns: '1fr 1fr' }}>

      {/* ── Left panel — brand ─────────────────────────────────────── */}
      <div className="login-brand" style={{ background: 'transparent', borderRight: `1px solid ${C.border}`, padding: '52px 56px', display: 'flex', flexDirection: 'column', position: 'relative', overflow: 'hidden' }}>

        {/* Logo */}
        <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: 10, textDecoration: 'none', color: C.white, marginBottom: 64, position: 'relative', zIndex: 1 }}>
          <svg width="38" height="38" viewBox="0 0 512 512" fill="none">
            <defs>
              <linearGradient id="lbg" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stopColor="#0EA5E9"/><stop offset="100%" stopColor="#0369A1"/></linearGradient>
              <linearGradient id="llf" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stopColor="#84CC16"/><stop offset="100%" stopColor="#3F6212"/></linearGradient>
            </defs>
            <path d="M256 56 C390 56 456 150 456 256" fill="none" stroke="url(#lbg)" strokeWidth="28" strokeLinecap="round"/>
            <path d="M56 256 C56 150 122 56 256 56" fill="none" stroke="url(#lbg)" strokeWidth="28" strokeLinecap="round"/>
            <circle cx="256" cy="180" r="38" fill="url(#lbg)"/>
            <path d="M170 260 Q256 340 256 320 Q256 340 342 260" fill="url(#lbg)"/>
            <path d="M256 340 Q140 340 100 420 Q180 380 256 456 Z" fill="url(#llf)"/>
            <path d="M256 340 Q372 340 412 420 Q332 380 256 456 Z" fill="url(#llf)"/>
          </svg>
          <span style={{ fontSize: 18, fontWeight: 800, letterSpacing: '-0.02em', lineHeight: 1.2, color: '#22c55e' }}>EIS</span>
        </Link>

        <div style={{ flex: 1, position: 'relative', zIndex: 1, textAlign: 'left' }}>
          <h2 style={{ fontSize: 'clamp(26px, 2.5vw, 38px)', fontWeight: 800, lineHeight: 1.15, letterSpacing: '-0.025em', marginBottom: 16 }}>
            Your compliance<br/>
            <span style={{ background: `linear-gradient(135deg, ${C.lime}, ${C.cyan})`, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
              command centre
            </span>
          </h2>
          <p style={{ color: C.gray2, fontSize: 15, lineHeight: 1.7, marginBottom: 48, maxWidth: 360 }}>
            Sign in to manage your frameworks, evidence, audits, and ESG tracking — all in one AI-enhanced platform.
          </p>

          {/* Feature list */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 22, marginBottom: 52 }}>
            {[
              { icon: <Settings size={20} />, title: 'Framework Management', desc: 'ISO 27001 · SOC 2 · GDPR · NIST · 50+ more' },
              { icon: <Search size={20} />, title: 'Audit & Certification', desc: 'Internal audits, external certs, evidence packs' },
              { icon: <Bot size={20} />, title: 'AI Compliance Intelligence', desc: 'Proactive risk evaluation and persona-aware insights' },
              { icon: <Sprout size={20} />, title: 'ESG & Carbon Tracking', desc: 'Scope 1/2/3 emissions alongside your frameworks' },
            ].map(item => (
              <div key={item.title} style={{ display: 'flex', gap: 14 }}>
                <span style={{ flexShrink: 0, marginTop: 2, color: C.lime }}>{item.icon}</span>
                <div>
                  <div style={{ fontWeight: 600, fontSize: 14 }}>{item.title}</div>
                  <div style={{ color: C.gray3, fontSize: 12, marginTop: 3 }}>{item.desc}</div>
                </div>
              </div>
            ))}
          </div>

          {/* Product positioning block — replaces a fabricated testimonial.
              Do not reintroduce without a real customer + written consent. */}
          <div style={{ background: `${C.lime}08`, border: `1px solid ${C.lime}20`, borderRadius: 14, padding: '20px 22px' }}>
            <p style={{ fontSize: 13, color: C.gray1, lineHeight: 1.7, marginBottom: 4 }}>
              One platform for the full compliance lifecycle — define frameworks, assign
              requirements, collect evidence, run audits, and issue certificates under a single audit trail.
            </p>
          </div>
        </div>

        {/* Bottom badges — frameworks the platform supports, not Yiontech accreditation */}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 40, position: 'relative', zIndex: 1 }}>
          {['ISO 27001', 'SOC 2', 'UK GDPR', 'ISO 14001'].map(b => (
            <span key={b} style={{ fontSize: 10, color: C.gray3, border: `1px solid ${C.border}`, borderRadius: 4, padding: '3px 8px', fontWeight: 600 }}>{b}</span>
          ))}
        </div>
      </div>

      {/* ── Right panel — form ─────────────────────────────────────── */}
      <div className="login-form-panel" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '52px 56px' }}>
        <div style={{ width: '100%', maxWidth: 420 }}>

          {/* Header */}
          <div style={{ marginBottom: 40, textAlign: 'left' }}>
            <h1 style={{ fontSize: 28, fontWeight: 800, letterSpacing: '-0.025em', marginBottom: 8 }}>Welcome back</h1>
            <p style={{ color: C.gray2, fontSize: 14 }}>Enter your details to access your account.</p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: 20 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: C.gray2, marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Email address</label>
              <input
                id="email" name="email" type="email" required
                value={credentials.email} onChange={handleInputChange}
                placeholder="name@company.com"
                style={inputStyle}
              />
            </div>

            <div style={{ marginBottom: 8 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                <label style={{ fontSize: 12, fontWeight: 600, color: C.gray2, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Password</label>
                <Link to="/forgot-password" style={{ fontSize: 12, color: C.lime, textDecoration: 'none', fontWeight: 600 }}>Forgot password?</Link>
              </div>
              <div style={{ position: 'relative' }}>
                <input
                  id="password" name="password"
                  type={showPassword ? 'text' : 'password'}
                  required
                  value={credentials.password} onChange={handleInputChange}
                  placeholder="••••••••"
                  style={{ ...inputStyle, paddingRight: 44 }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(p => !p)}
                  style={{ position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: C.gray3, cursor: 'pointer', display: 'flex', alignItems: 'center' }}
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            {/* Error */}
            {loginError && (
              <div style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 10, padding: '12px 16px', marginBottom: 20, marginTop: 16, fontSize: 13, color: '#fca5a5', display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ flexShrink: 0, marginTop: 1, color: '#f59e0b' }}><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
                <span>{loginError}</span>
              </div>
            )}

            <button
              type="submit"
              disabled={isLoggingIn}
              style={{
                width: '100%', marginTop: 28,
                backgroundColor: isLoggingIn ? C.bg3 : C.lime,
                color: isLoggingIn ? C.gray3 : '#000',
                border: 'none', borderRadius: 10, padding: '13px 20px',
                fontSize: 15, fontWeight: 700,
                cursor: isLoggingIn ? 'not-allowed' : 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                transition: 'all 0.15s',
              }}
            >
              {isLoggingIn ? (
                <>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ animation: 'spin 1s linear infinite' }}>
                    <circle cx="8" cy="8" r="6" stroke={C.gray3} strokeWidth="2" strokeDasharray="20 12" />
                  </svg>
                  Signing in…
                </>
              ) : 'Sign in'}
            </button>
          </form>

          {/* Demo Accounts */}
          {demoAccounts.length > 0 && (() => {
            const roleColors: Record<string, string> = {
              'System Admin':     '#67e8f9',
              'Company Admin':    '#22c55e',
              'Compliance User':  '#3b82f6',
              'Internal Auditor': '#f59e0b',
              'External Auditor': '#a855f7',
              'Partner Admin':    '#8b5cf6',
            };
            const platformAccounts = demoAccounts.filter(a => a.group === 'platform');
            const partnerAccounts  = demoAccounts.filter(a => a.group === 'partner');
            const portfolioAccounts = demoAccounts.filter(a => a.group === 'partner_portfolio');
            const rightAccounts = [...partnerAccounts, ...portfolioAccounts];

            const renderCard = (account: DemoAccount) => {
              const color = roleColors[account.role] || C.cyan;
              const isLoading = demoLoading === account.email;
              return (
                <button
                  key={account.email}
                  onClick={() => handleDemoLogin(account.email)}
                  disabled={!!demoLoading}
                  style={{
                    display: 'flex', alignItems: 'flex-start', gap: 10,
                    padding: '9px 11px', borderRadius: 9, width: '100%',
                    background: isLoading ? `${color}15` : C.card,
                    border: `1px solid ${isLoading ? `${color}40` : C.border}`,
                    cursor: demoLoading ? 'not-allowed' : 'pointer',
                    transition: 'all 0.15s', textAlign: 'left',
                    opacity: demoLoading && !isLoading ? 0.5 : 1,
                  }}
                >
                  <div style={{
                    width: 28, height: 28, borderRadius: 6, flexShrink: 0,
                    background: `${color}20`, display: 'flex',
                    alignItems: 'center', justifyContent: 'center', marginTop: 1,
                  }}>
                    {isLoading ? (
                      <svg width="12" height="12" viewBox="0 0 16 16" fill="none" style={{ animation: 'spin 1s linear infinite' }}>
                        <circle cx="8" cy="8" r="6" stroke={color} strokeWidth="2" strokeDasharray="20 12" />
                      </svg>
                    ) : (
                      <Play size={11} color={color} fill={color} />
                    )}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: C.white, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {account.name}
                    </div>
                    <div style={{ fontSize: 10, fontWeight: 700, color, marginTop: 1 }}>{account.role}</div>
                    {account.company && (
                      <div style={{ fontSize: 10, color: C.gray3, marginTop: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {account.company}
                      </div>
                    )}
                  </div>
                </button>
              );
            };

            return (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: 14, margin: '28px 0' }}>
                  <div style={{ flex: 1, height: 1, backgroundColor: C.border }} />
                  <span style={{ fontSize: 12, color: C.cyan, fontWeight: 600 }}>Try the Demo</span>
                  <div style={{ flex: 1, height: 1, backgroundColor: C.border }} />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 24 }}>
                  {/* Left: Platform accounts */}
                  <div>
                    <div style={{ fontSize: 10, fontWeight: 700, color: C.gray3, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 }}>
                      Platform
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                      {platformAccounts.length > 0
                        ? platformAccounts.map(renderCard)
                        : <div style={{ fontSize: 11, color: C.gray3, padding: '8px 0', fontStyle: 'italic' }}>No platform accounts</div>
                      }
                    </div>
                  </div>

                  {/* Right: Partner + Portfolio accounts */}
                  <div>
                    <div style={{ fontSize: 10, fontWeight: 700, color: C.gray3, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 }}>
                      Partner
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                      {rightAccounts.map(renderCard)}
                    </div>
                  </div>
                </div>
              </>
            );
          })()}

          {/* Divider - only show when no demo accounts */}
          {demoAccounts.length === 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 14, margin: '28px 0' }}>
              <div style={{ flex: 1, height: 1, backgroundColor: C.border }} />
              <span style={{ fontSize: 12, color: C.gray3 }}>or</span>
              <div style={{ flex: 1, height: 1, backgroundColor: C.border }} />
            </div>
          )}

          {/* Security badge */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: 8, marginBottom: 36 }}>
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M7 1L2 3v4c0 2.76 2.13 5.34 5 5.96C14.87 12.34 12 9.76 12 7V3L7 1z" fill={C.lime} fillOpacity="0.2" stroke={C.lime} strokeWidth="1.2"/>
              <path d="M5 7l1.5 1.5L9 5.5" stroke={C.lime} strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <span style={{ fontSize: 12, color: C.gray3, fontWeight: 500 }}>AES-256 encrypted · SOC 2 certified infrastructure</span>
          </div>

          {/* Sign up link */}
          <p style={{ textAlign: 'left', fontSize: 13, color: C.gray3 }}>
            Don't have an account?{' '}
            <Link to="/register" style={{ color: C.lime, textDecoration: 'none', fontWeight: 600 }}>Request trial access</Link>
          </p>

          <p style={{ textAlign: 'left', fontSize: 12, color: C.gray3, marginTop: 40 }}>© 2026 EIS Tech Ltd. All rights reserved.</p>
        </div>
      </div>

      {/* Spin keyframe + responsive */}
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @media (max-width: 768px) {
          .login-root { grid-template-columns: 1fr !important; }
          .login-brand { display: none !important; }
          .login-form-panel { padding: 40px 24px !important; }
        }
      `}</style>
    </div>
    </div>
  );
};

export default Login;
